import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { I } from "../icons";
import { EmptyState, LoadingState } from "../shared/States";
import { VariantsManager } from "./VariantsManager";
import { ProductRelationsManager } from "./ProductRelationsManager";
import { DOP, fmtDop } from "../helpers";

const CATEGORIES = ["protein", "creatine", "vitamins", "supplements", "snacks", "drinks", "wellness", "other"];
const BUCKET = "product-images";
// Prices in DB are stored in DOP (matches the CSV uploaded by SKU).
// Admin grid shows them raw via the shared `fmtDop` helper — no USD→DOP conversion needed.

/**
 * ProductManager — unified, intuitive catalog editor for Admin + Vendor.
 *
 * Features:
 *  - Search + filter (category, active state, low-stock)
 *  - Inline edit price/stock (click value → input → Enter saves)
 *  - Toggle active/inactive (single-click status pill)
 *  - Drawer form for create/edit (full fields, validated)
 *  - Image upload with crop (max 800×800, JPEG) OR external URL
 *  - Bulk actions: activate / deactivate / delete selected
 *  - CSV import (admin only) with preview
 *  - CSV export
 *  - Realtime: rows refresh when any admin edits a product
 */
export function ProductManager({ mode, user, lang, t, $, toast }) {
  const isAdmin = mode === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterActive, setFilterActive] = useState("all"); // all | active | inactive
  const [filterLow, setFilterLow] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [inlineEdit, setInlineEdit] = useState({});

  // Form / drawer
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Crop modal
  const [cropSrc, setCropSrc] = useState(null);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const cropImgRef = useRef(null);
  const cropDragRef = useRef(null);

  // CSV import (admin)
  const [csvPreview, setCsvPreview] = useState(null);
  const csvInputRef = useRef(null);

  function emptyForm() {
    return {
      name: "", brand: "", sku: "", price: "", compare_at_price: "",
      stock_quantity: "", low_stock_threshold: "10", category: "",
      description: "", images: [], is_active: true,
      show_in_trending: false, show_in_deals: false,
      cost_price: "", itbis_exempt: false, itbis_rate: "18",
      // Extras (stored in nutrition_facts jsonb)
      ingredients: "", usage: "", warnings: "", allergens: "",
      timing: "", benefits: "",
      nut_serving: "", nut_calories: "", nut_protein: "", nut_carbs: "",
      nut_fat: "", nut_sugar: "", nut_sodium: "", nut_fiber: "",
    };
  }

  // ───── Load ─────
  const load = useCallback(async () => {
    let q = supabase.from("products").select("*").order("created_at", { ascending: false });
    if (!isAdmin) q = q.eq("vendor_id", user.id);
    const { data, error } = await q;
    if (error) { console.error(error); toast(`❌ ${error.message}`); return; }
    setRows(data || []);
  }, [isAdmin, user?.id, toast]);

  useEffect(() => {
    if (!user?.id && !isAdmin) return;
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [user?.id, isAdmin, load]);

  // Realtime: subscribe to product changes
  useEffect(() => {
    const ch = supabase.channel(`pm-products-${mode}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "products" }, () => { load(); })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [mode, load]);

  // ───── Filtering ─────
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter(p => {
      if (s && !(`${p.name} ${p.brand || ""} ${p.sku || ""}`.toLowerCase().includes(s))) return false;
      if (filterCat && p.category !== filterCat) return false;
      if (filterActive === "active" && !p.is_active) return false;
      if (filterActive === "inactive" && p.is_active) return false;
      if (filterLow && (p.stock_quantity ?? 0) > (p.low_stock_threshold ?? 10)) return false;
      return true;
    });
  }, [rows, search, filterCat, filterActive, filterLow]);

  // ───── Image upload + crop ─────
  const onPickImage = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast(`❌ ${lang === "es" ? "Solo imágenes" : "Images only"}`); return; }
    if (file.size > 10 * 1024 * 1024) { toast(`❌ ${lang === "es" ? "Máximo 10MB" : "Max 10MB"}`); return; }
    const reader = new FileReader();
    reader.onload = () => { setCropSrc(reader.result); setCropZoom(1); setCropOffset({ x: 0, y: 0 }); };
    reader.readAsDataURL(file);
  };

  const confirmCrop = async () => {
    const img = cropImgRef.current;
    if (!img || !cropSrc) return;
    setUploading(true);
    try {
      const OUT = 800, VIEW = 320;
      const baseScale = Math.min(VIEW / img.naturalWidth, VIEW / img.naturalHeight);
      const scale = baseScale * cropZoom;
      const drawW = img.naturalWidth * scale, drawH = img.naturalHeight * scale;
      const imgX = (VIEW - drawW) / 2 + cropOffset.x;
      const imgY = (VIEW - drawH) / 2 + cropOffset.y;
      const sx = Math.max(0, (-imgX) / scale);
      const sy = Math.max(0, (-imgY) / scale);
      const sSize = Math.min(img.naturalWidth - sx, img.naturalHeight - sy, VIEW / scale);

      const canvas = document.createElement("canvas");
      canvas.width = OUT; canvas.height = OUT;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, OUT, OUT);
      ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUT, OUT);
      const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("Canvas export failed");

      const folder = isAdmin ? "admin" : user.id;
      const path = `${folder}/${crypto.randomUUID()}.jpg`;
      const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { cacheControl: "3600", upsert: false, contentType: "image/jpeg" });
      if (error) throw error;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      setForm(f => ({ ...f, images: [...(f.images || []), pub.publicUrl] }));
      setCropSrc(null);
      toast(`✅ ${lang === "es" ? "Imagen subida" : "Image uploaded"}`);
    } catch (e) {
      toast(`❌ ${e.message || e}`);
    } finally { setUploading(false); }
  };

  const onCropPointerDown = (e) => {
    cropDragRef.current = { startX: e.clientX, startY: e.clientY, ox: cropOffset.x, oy: cropOffset.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onCropPointerMove = (e) => {
    const d = cropDragRef.current; if (!d) return;
    setCropOffset({ x: d.ox + (e.clientX - d.startX), y: d.oy + (e.clientY - d.startY) });
  };
  const onCropPointerUp = () => { cropDragRef.current = null; };

  // ───── CRUD ─────
  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    const nf = p.nutrition_facts || {};
    const imgs = Array.isArray(p.images) && p.images.length > 0
      ? p.images
      : (p.image_url ? [p.image_url] : []);
    setForm({
      name: p.name || "", brand: p.brand || "", sku: p.sku || "",
      price: p.price ?? "", compare_at_price: p.compare_at_price ?? "",
      stock_quantity: p.stock_quantity ?? "",
      low_stock_threshold: p.low_stock_threshold ?? 10,
      category: p.category || "",
      description: p.description || "",
      images: imgs,
      is_active: p.is_active ?? true,
      show_in_trending: !!p.show_in_trending,
      show_in_deals: !!p.show_in_deals,
      cost_price: p.cost_price ?? "",
      itbis_exempt: !!p.itbis_exempt,
      itbis_rate: p.itbis_rate ?? "18",
      ingredients: nf.ingredients || "",
      usage: nf.usage || "",
      warnings: nf.warnings || "",
      allergens: nf.allergens || "",
      timing: nf.timing || "",
      benefits: Array.isArray(nf.benefits) ? nf.benefits.join("\n") : (nf.benefits || ""),
      nut_serving: nf.serving ?? "",
      nut_calories: nf.calories ?? "",
      nut_protein: nf.protein ?? "",
      nut_carbs: nf.carbs ?? "",
      nut_fat: nf.fat ?? "",
      nut_sugar: nf.sugar ?? "",
      nut_sodium: nf.sodium ?? "",
      nut_fiber: nf.fiber ?? "",
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim() || form.price === "" || isNaN(parseFloat(form.price))) {
      toast(`❌ ${lang === "es" ? "Nombre y precio son requeridos" : "Name and price are required"}`);
      return;
    }
    const imgs = (form.images || []).filter(Boolean);
    if (imgs.length === 0) {
      toast(`❌ ${lang === "es" ? "Debes agregar al menos una imagen y marcarla como principal" : "You must add at least one image and mark it as the main one"}`);
      return;
    }
    // Build nutrition_facts JSON with numeric + extras (omit empty)
    const numOrNull = (v) => v === "" || v == null ? null : (isNaN(parseFloat(v)) ? null : parseFloat(v));
    const nf: any = {};
    if (form.nut_serving) nf.serving = String(form.nut_serving);
    const numKeys: [string, string][] = [
      ["calories","nut_calories"],["protein","nut_protein"],["carbs","nut_carbs"],
      ["fat","nut_fat"],["sugar","nut_sugar"],["sodium","nut_sodium"],["fiber","nut_fiber"],
    ];
    for (const [k, fk] of numKeys) { const v = numOrNull(form[fk]); if (v != null) nf[k] = v; }
    if (form.ingredients?.trim()) nf.ingredients = form.ingredients.trim();
    if (form.usage?.trim()) nf.usage = form.usage.trim();
    if (form.warnings?.trim()) nf.warnings = form.warnings.trim();
    if (form.allergens?.trim()) nf.allergens = form.allergens.trim();
    if (form.timing?.trim()) nf.timing = form.timing.trim();
    const benefitsArr = (form.benefits || "").split("\n").map(s => s.trim()).filter(Boolean);
    if (benefitsArr.length) nf.benefits = benefitsArr;

    const payload: any = {
      name: form.name.trim(),
      brand: form.brand.trim() || null,
      sku: form.sku.trim() || null,
      price: parseFloat(form.price),
      compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
      stock_quantity: form.stock_quantity !== "" ? parseInt(form.stock_quantity, 10) : 0,
      low_stock_threshold: form.low_stock_threshold !== "" ? Math.max(0, parseInt(form.low_stock_threshold, 10) || 0) : 10,
      cost_price: form.cost_price !== "" ? parseFloat(form.cost_price) : null,
      itbis_exempt: !!form.itbis_exempt,
      itbis_rate: form.itbis_rate !== "" ? parseFloat(form.itbis_rate) : 18,
      category: form.category || null,
      description: form.description.trim() || null,
      image_url: imgs[0] || null,
      images: imgs.length ? imgs : null,
      nutrition_facts: Object.keys(nf).length ? nf : null,
      is_active: !!form.is_active,
      show_in_trending: !!form.show_in_trending,
      show_in_deals: !!form.show_in_deals,
    };
    // Vendor must always own; admin only sets vendor_id on create if missing
    if (!isAdmin) payload.vendor_id = user.id;
    else if (!editing) payload.vendor_id = user?.id || null;

    const { error } = editing
      ? await supabase.from("products").update(payload).eq("id", editing.id)
      : await supabase.from("products").insert(payload);
    if (error) { toast(`❌ ${error.message}`); return; }
    toast(`✅ ${editing ? (lang === "es" ? "Actualizado" : "Updated") : (lang === "es" ? "Creado" : "Created")}`);
    setShowForm(false); setEditing(null); load();
  };

  const remove = async (p) => {
    if (!confirm(lang === "es" ? `¿Eliminar "${p.name}"?` : `Delete "${p.name}"?`)) return;
    const { error } = await supabase.from("products").delete().eq("id", p.id);
    if (error) { toast(`❌ ${error.message}`); return; }
    toast(`🗑️ ${lang === "es" ? "Eliminado" : "Deleted"}`);
    load();
  };

  const toggleActive = async (p) => {
    const { error } = await supabase.from("products").update({ is_active: !p.is_active }).eq("id", p.id);
    if (error) { toast(`❌ ${error.message}`); return; }
    setRows(prev => prev.map(x => x.id === p.id ? { ...x, is_active: !x.is_active } : x));
  };

  // ───── Inline edit ─────
  const startInline = (pid, field, current) => setInlineEdit(p => ({ ...p, [pid]: { field, value: String(current ?? "") } }));
  const cancelInline = (pid) => setInlineEdit(p => { const n = { ...p }; delete n[pid]; return n; });
  const commitInline = async (p) => {
    const edit = inlineEdit[p.id]; if (!edit) return;
    const numeric = parseFloat(edit.value);
    if (isNaN(numeric) || numeric < 0) { toast(`❌ ${lang === "es" ? "Valor inválido" : "Invalid value"}`); return; }
    const updates = edit.field === "stock" ? { stock_quantity: Math.floor(numeric) }
      : edit.field === "cost" ? { cost_price: numeric }
      : { price: numeric };
    const { error } = await supabase.from("products").update(updates).eq("id", p.id);
    if (error) { toast(`❌ ${error.message}`); return; }
    cancelInline(p.id);
    setRows(prev => prev.map(x => x.id === p.id ? { ...x, ...updates } : x));
    toast(`✅ ${lang === "es" ? "Actualizado" : "Updated"}`);
  };

  // ───── Bulk ─────
  const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selAllFiltered = () => setSelected(new Set(filtered.map(p => p.id)));
  const clearSel = () => setSelected(new Set());
  const bulk = async (action) => {
    const ids = Array.from(selected) as string[]; if (!ids.length) return;
    if (action === "delete" && !confirm(lang === "es" ? `¿Eliminar ${ids.length} productos?` : `Delete ${ids.length} products?`)) return;
    let q;
    if (action === "activate") q = supabase.from("products").update({ is_active: true }).in("id", ids);
    else if (action === "deactivate") q = supabase.from("products").update({ is_active: false }).in("id", ids);
    else q = supabase.from("products").delete().in("id", ids);
    const { error } = await q;
    if (error) { toast(`❌ ${error.message}`); return; }
    toast(`✅ ${ids.length} ${lang === "es" ? "actualizados" : "updated"}`);
    clearSel(); load();
  };

  // ───── CSV import (admin) ─────
  const onCsvPick = async (file) => {
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) { toast(`❌ ${lang === "es" ? "CSV vacío" : "Empty CSV"}`); return; }
    // Normalize headers: lowercase, strip accents, map Spanish aliases to canonical keys
    const norm = (s) => s.toLowerCase().trim()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/\s*\(.*?\)\s*/g, "").trim();
    const aliasMap = {
      "nombre": "name", "producto": "name", "name": "name",
      "marca": "brand", "brand": "brand",
      "sku": "sku", "codigo": "sku", "código": "sku",
      "categoria": "category", "category": "category",
      "precio venta": "price", "precio": "price", "precio venta dop": "price", "price": "price",
      "precio compra": "cost_price", "precio compra dop": "cost_price", "costo": "cost_price", "cost": "cost_price", "cost_price": "cost_price",
      "compare_at_price": "compare_at_price", "precio comparacion": "compare_at_price",
      "stock actual": "stock_quantity", "stock": "stock_quantity", "stock_quantity": "stock_quantity", "existencia": "stock_quantity",
      "stock minimo": "low_stock_threshold", "stock min": "low_stock_threshold", "stock_min": "low_stock_threshold", "low_stock_threshold": "low_stock_threshold",
      "exento itbis": "itbis_exempt", "itbis exento": "itbis_exempt", "itbis_exempt": "itbis_exempt",
      "tasa itbis": "itbis_rate", "itbis": "itbis_rate", "tasa itbis %": "itbis_rate", "itbis_rate": "itbis_rate",
      "descripcion": "description", "description": "description",
      "image_url": "image_url", "imagen": "image_url", "url imagen": "image_url",
      "is_active": "is_active", "activo": "is_active",
    };
    const rawHeaders = parseCsvLine(lines[0]).map(h => norm(h));
    const headers = rawHeaders.map(h => aliasMap[h] || h);
    const required = ["name", "price"];
    const missing = required.filter(r => !headers.includes(r));
    if (missing.length) {
      toast(`❌ ${lang === "es" ? "Faltan columnas" : "Missing columns"}: ${missing.join(", ")} — ${lang === "es" ? "encontradas" : "found"}: ${rawHeaders.join(", ")}`);
      return;
    }
    const rowsParsed = lines.slice(1).map(l => {
      const cells = parseCsvLine(l);
      const o = {}; headers.forEach((h, i) => o[h] = cells[i] || "");
      return o;
    });
    setCsvPreview(rowsParsed);
  };
  const confirmCsvImport = async () => {
    if (!csvPreview?.length) return;
    // CSV prices come in DOP and are stored in DOP as-is (1:1 with what
    // the user uploads). The storefront converts DOP→USD at read time.
    const dopAsIs = (v) => {
      const n = parseFloat(v);
      if (!isFinite(n)) return null;
      return Math.round(n * 100) / 100;
    };
    const payload = csvPreview.map(r => ({
      name: r.name?.trim(),
      brand: r.brand?.trim() || null,
      sku: r.sku?.trim() || null,
      price: dopAsIs(r.price) || 0,
      compare_at_price: r.compare_at_price ? dopAsIs(r.compare_at_price) : null,
      stock_quantity: r.stock ? parseInt(r.stock, 10) : (r.stock_quantity ? parseInt(r.stock_quantity, 10) : 0),
      category: r.category?.trim() || null,
      description: r.description?.trim() || null,
      image_url: r.image_url?.trim() || null,
      is_active: r.is_active === undefined ? true : /^(true|1|yes|sí|si)$/i.test(String(r.is_active).trim()),
      cost_price: r.cost_price ? dopAsIs(r.cost_price) : null,
      low_stock_threshold: r.low_stock_threshold ? parseInt(r.low_stock_threshold, 10) : 0,
      itbis_exempt: r.itbis_exempt === undefined ? false : /^(true|1|yes|sí|si|s)$/i.test(String(r.itbis_exempt).trim()),
      itbis_rate: r.itbis_rate ? parseFloat(r.itbis_rate) : 18.00,
      vendor_id: user?.id || null,
    })).filter(p => p.name && p.price >= 0);
    const { error } = await supabase.from("products").insert(payload);
    if (error) { toast(`❌ ${error.message}`); return; }
    toast(`✅ ${payload.length} ${lang === "es" ? "importados" : "imported"}`);
    setCsvPreview(null); load();
  };

  // ───── CSV export ─────
  const exportCsv = () => {
    const headers = ["name", "brand", "sku", "price", "compare_at_price", "stock_quantity", "low_stock_threshold", "cost_price", "itbis_exempt", "itbis_rate", "category", "is_active", "image_url"];
    const csv = [
      headers.join(","),
      ...filtered.map(p => headers.map(h => csvCell(p[h])).join(",")),
    ].join("\n");
    download(`${mode}-products-${new Date().toISOString().split("T")[0]}.csv`, csv);
  };

  // ───── Render ─────
  const lowCount = rows.filter(p => (p.stock_quantity ?? 0) <= (p.low_stock_threshold ?? 10)).length;
  const activeCount = rows.filter(p => p.is_active).length;

  return (
    <>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button className="btn bp" onClick={openCreate}>
          ＋ {lang === "es" ? "Nuevo producto" : "New product"}
        </button>
        {isAdmin && (
          <>
            <input ref={csvInputRef} type="file" accept=".csv,text/csv" style={{ display: "none" }}
              onChange={e => { onCsvPick(e.target.files?.[0]); e.target.value = ""; }} />
            <button className="btn bg bs" style={{ fontSize: 11 }} onClick={() => csvInputRef.current?.click()}>
              ⬆ {lang === "es" ? "Importar CSV" : "Import CSV"}
            </button>
          </>
        )}
        <button className="btn bg bs" style={{ fontSize: 11 }} onClick={exportCsv}>
          ⬇ {lang === "es" ? "Exportar CSV" : "Export CSV"}
        </button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: "var(--tx3)" }}>
          {rows.length} {lang === "es" ? "total" : "total"} · {activeCount} {lang === "es" ? "activos" : "active"} · {lowCount} {lang === "es" ? "stock bajo" : "low stock"}
        </span>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={lang === "es" ? "🔍 Buscar nombre, marca, SKU…" : "🔍 Search name, brand, SKU…"}
          style={{ ...inputSm, flex: 1, minWidth: 200 }} />
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={inputSm}>
          <option value="">{lang === "es" ? "Todas las categorías" : "All categories"}</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterActive} onChange={e => setFilterActive(e.target.value)} style={inputSm}>
          <option value="all">{lang === "es" ? "Todos" : "All"}</option>
          <option value="active">{lang === "es" ? "Solo activos" : "Active only"}</option>
          <option value="inactive">{lang === "es" ? "Solo inactivos" : "Inactive only"}</option>
        </select>
        <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4, color: "var(--tx3)" }}>
          <input type="checkbox" checked={filterLow} onChange={e => setFilterLow(e.target.checked)} />
          {lang === "es" ? "Stock bajo" : "Low stock"}
        </label>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 12px", background: "var(--bg2)", border: "1px solid var(--bp)", borderRadius: 8, marginBottom: 10 }}>
          <b style={{ fontSize: 12 }}>{selected.size} {lang === "es" ? "seleccionados" : "selected"}</b>
          <button className="btn bg bs" style={{ fontSize: 10 }} onClick={() => bulk("activate")}>{lang === "es" ? "Activar" : "Activate"}</button>
          <button className="btn bg bs" style={{ fontSize: 10 }} onClick={() => bulk("deactivate")}>{lang === "es" ? "Desactivar" : "Deactivate"}</button>
          <button className="btn bs" style={{ fontSize: 10, color: "var(--am)" }} onClick={() => bulk("delete")}>🗑️ {lang === "es" ? "Borrar" : "Delete"}</button>
          <div style={{ flex: 1 }} />
          <button className="btn bs" style={{ fontSize: 10 }} onClick={clearSel}>{lang === "es" ? "Cancelar" : "Clear"}</button>
        </div>
      )}

      <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 6 }}>
        💡 {lang === "es" ? "Click en precio/stock/costo para editar al instante · click en estado para activar/desactivar" : "Click price/stock/cost to edit inline · click status to toggle"}
      </div>

      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 28 }}>
              <input type="checkbox"
                checked={filtered.length > 0 && selected.size === filtered.length}
                onChange={e => e.target.checked ? selAllFiltered() : clearSel()} />
            </th>
            <th>{lang === "es" ? "Producto" : "Product"}</th>
            <th>SKU</th>
            <th>{lang === "es" ? "Precio" : "Price"}</th>
            <th>{lang === "es" ? "Costo" : "Cost"}</th>
            <th>{lang === "es" ? "Stock" : "Stock"}</th>
            <th>{lang === "es" ? "Stock mín." : "Min stock"}</th>
            <th>{lang === "es" ? "Categoría" : "Category"}</th>
            <th>ITBIS</th>
            <th>{lang === "es" ? "Estado" : "Status"}</th>
            <th>{lang === "es" ? "Acción" : "Action"}</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={11} style={{ padding: 0 }}><LoadingState label={lang === "es" ? "Cargando…" : "Loading…"} /></td></tr>
          ) : filtered.length === 0 ? (
            <tr><td colSpan={11} style={{ padding: 0 }}>
              <EmptyState icon="📦"
                title={rows.length === 0
                  ? (lang === "es" ? "Sin productos aún" : "No products yet")
                  : (lang === "es" ? "Sin resultados" : "No matches")}
                description={rows.length === 0
                  ? (lang === "es" ? "Crea tu primer producto para empezar." : "Create your first product to get started.")
                  : (lang === "es" ? "Ajusta los filtros." : "Adjust the filters.")}
                action={rows.length === 0 && <button className="btn bp" onClick={openCreate}>＋ {lang === "es" ? "Nuevo" : "New"}</button>}
              />
            </td></tr>
          ) : filtered.map(p => {
            const ie = inlineEdit[p.id];
            const isSel = selected.has(p.id);
            return (
              <tr key={p.id} style={isSel ? { background: "var(--bg2)" } : undefined}>
                <td><input type="checkbox" checked={isSel} onChange={() => toggleSel(p.id)} /></td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    {p.image_url
                      ? <img src={p.image_url} alt="" style={{ width: 28, height: 28, objectFit: "contain", borderRadius: 4, background: "var(--bg)" }} />
                      : <div style={{ width: 28, height: 28, background: "var(--bg2)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>📦</div>}
                    <div>
                      <b style={{ fontSize: 12 }}>{p.name}</b>
                      <div style={{ fontSize: 10, color: "var(--tx3)" }}>{p.brand || "—"}</div>
                    </div>
                  </div>
                </td>
                <td style={{ fontFamily: "monospace", fontSize: 10 }}>{p.sku || "—"}</td>
                <td style={{ fontWeight: 600 }}>
                  {ie?.field === "price"
                    ? <InlineInput value={ie.value} onChange={v => setInlineEdit(prev => ({ ...prev, [p.id]: { ...prev[p.id], value: v } }))} onSave={() => commitInline(p)} onCancel={() => cancelInline(p.id)} />
                    : <span style={{ cursor: "pointer", borderBottom: "1px dashed var(--bd2)" }} onClick={() => startInline(p.id, "price", p.price)}>{fmtDop(p.price)}</span>}
                </td>
                <td style={{ fontWeight: 600 }}>
                  {ie?.field === "cost"
                    ? <InlineInput value={ie.value} onChange={v => setInlineEdit(prev => ({ ...prev, [p.id]: { ...prev[p.id], value: v } }))} onSave={() => commitInline(p)} onCancel={() => cancelInline(p.id)} />
                    : <span style={{ cursor: "pointer", borderBottom: "1px dashed var(--bd2)" }} onClick={() => startInline(p.id, "cost", p.cost_price)}>{fmtDop(p.cost_price)}</span>}
                </td>
                <td style={{ color: (p.stock_quantity ?? 0) === 0 ? "var(--am)" : (p.stock_quantity ?? 0) <= (p.low_stock_threshold ?? 10) ? "var(--am)" : "var(--ac)" }}>
                  {ie?.field === "stock"
                    ? <InlineInput value={ie.value} onChange={v => setInlineEdit(prev => ({ ...prev, [p.id]: { ...prev[p.id], value: v } }))} onSave={() => commitInline(p)} onCancel={() => cancelInline(p.id)} />
                    : <span style={{ cursor: "pointer", borderBottom: "1px dashed var(--bd2)" }} onClick={() => startInline(p.id, "stock", p.stock_quantity)} title={lang === "es" ? `Umbral bajo: ${p.low_stock_threshold ?? 10}` : `Low threshold: ${p.low_stock_threshold ?? 10}`}>{p.stock_quantity ?? 0}</span>}
                </td>
                <td style={{ fontSize: 10, color: "var(--tx3)" }}>{p.low_stock_threshold ?? 10}</td>
                <td style={{ fontSize: 10, color: "var(--tx3)" }}>{p.category || "—"}</td>
                <td style={{ fontSize: 10 }}>
                  {p.itbis_exempt
                    ? <span style={{ color: "var(--ac)" }}>{lang === "es" ? "Exento" : "Exempt"}</span>
                    : <span>{p.itbis_rate ?? 18}%</span>}
                </td>
                <td>
                  <span className={`stb ${p.is_active ? "Active" : "Pending"}`} style={{ cursor: "pointer" }} onClick={() => toggleActive(p)}>
                    {p.is_active ? (lang === "es" ? "Activo" : "Active") : (lang === "es" ? "Inactivo" : "Inactive")}
                  </span>
                </td>
                <td>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button className="btn bg bs" style={{ fontSize: 10 }} onClick={() => openEdit(p)}>{lang === "es" ? "Editar" : "Edit"}</button>
                    <button className="btn bs" style={{ fontSize: 10, color: "var(--am)" }} onClick={() => remove(p)}>🗑️</button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* FORM MODAL */}
      {showForm && (
        <div className="qv-overlay" onClick={() => setShowForm(false)} style={{ zIndex: 1000 }}>
          <div className="qv-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 580, padding: 22 }}>
            <button className="qv-close" onClick={() => setShowForm(false)}>{I.x}</button>
            <h3 style={{ marginTop: 0, marginBottom: 14 }}>
              {editing ? (lang === "es" ? "Editar producto" : "Edit product") : (lang === "es" ? "Nuevo producto" : "New product")}
            </h3>

            {/* Images: multi-upload + URL paste */}
            <div style={{ marginBottom: 14 }}>
              <label style={lblStyle}>
                {lang === "es" ? "Imágenes (la primera es la principal)" : "Images (first is the main one)"}
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                {(form.images || []).map((url, idx) => (
                  <div key={`${url}-${idx}`} style={{ position: "relative", width: 72, height: 72 }}>
                    <img src={url} alt="" style={{ width: 72, height: 72, objectFit: "contain", borderRadius: 6, background: "var(--bg)", padding: 4, border: idx === 0 ? "2px solid var(--bp)" : "1px solid var(--bd2)" }} />
                    {idx === 0 && (
                      <span style={{ position: "absolute", top: -6, left: -6, fontSize: 8, background: "var(--bp)", color: "#fff", padding: "1px 5px", borderRadius: 8, fontWeight: 700 }}>
                        {lang === "es" ? "PPAL" : "MAIN"}
                      </span>
                    )}
                    <div style={{ position: "absolute", top: -4, right: -4, display: "flex", gap: 2 }}>
                      {idx > 0 && (
                        <button type="button" title={lang === "es" ? "Hacer principal" : "Set as main"}
                          onClick={() => setForm(f => { const a = [...f.images]; const [it] = a.splice(idx, 1); a.unshift(it); return { ...f, images: a }; })}
                          style={{ width: 18, height: 18, borderRadius: "50%", border: "1px solid var(--bd2)", background: "var(--bg2)", cursor: "pointer", fontSize: 10, lineHeight: 1, padding: 0 }}>★</button>
                      )}
                      <button type="button" title={lang === "es" ? "Quitar" : "Remove"}
                        onClick={() => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }))}
                        style={{ width: 18, height: 18, borderRadius: "50%", border: "1px solid var(--am)", background: "var(--bg2)", color: "var(--am)", cursor: "pointer", fontSize: 11, lineHeight: 1, padding: 0 }}>×</button>
                    </div>
                  </div>
                ))}
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => { onPickImage(e.target.files?.[0]); e.target.value = ""; }} />
                <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()}
                  style={{ width: 72, height: 72, borderRadius: 6, border: "1px dashed var(--bd2)", background: "var(--bg2)", color: "var(--tx3)", cursor: "pointer", fontSize: 22 }}>
                  {uploading ? "…" : "＋"}
                </button>
              </div>
              <input
                placeholder={lang === "es" ? "…o pega una URL y presiona Enter" : "…or paste a URL and press Enter"}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    const v = (e.currentTarget.value || "").trim();
                    if (v) {
                      setForm(f => ({ ...f, images: [...(f.images || []), v] }));
                      e.currentTarget.value = "";
                    }
                    e.preventDefault();
                  }
                }}
                style={{ ...inputSm, width: "100%" }} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label={lang === "es" ? "Nombre *" : "Name *"} value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} colSpan={2} />
              <Field label={lang === "es" ? "Marca" : "Brand"} value={form.brand} onChange={v => setForm(f => ({ ...f, brand: v }))} />
              <Field label="SKU" value={form.sku} onChange={v => setForm(f => ({ ...f, sku: v }))} />
              <Field label={lang === "es" ? "Precio *" : "Price *"} type="number" value={form.price} onChange={v => setForm(f => ({ ...f, price: v }))} />
              <Field label={lang === "es" ? "Precio comparado" : "Compare-at"} type="number" value={form.compare_at_price} onChange={v => setForm(f => ({ ...f, compare_at_price: v }))} />
              <Field label="Stock" type="number" value={form.stock_quantity} onChange={v => setForm(f => ({ ...f, stock_quantity: v }))} />
              <Field label={lang === "es" ? "Umbral stock bajo" : "Low-stock threshold"} type="number" value={form.low_stock_threshold} onChange={v => setForm(f => ({ ...f, low_stock_threshold: v }))} />
              <Field label={lang === "es" ? "Costo" : "Cost"} type="number" value={form.cost_price} onChange={v => setForm(f => ({ ...f, cost_price: v }))} />
              <SelectField label={lang === "es" ? "Categoría" : "Category"} value={form.category} onChange={v => setForm(f => ({ ...f, category: v }))} options={CATEGORIES} />
              <Field label={lang === "es" ? "Descripción" : "Description"} value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} colSpan={2} multiline />
              <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={form.itbis_exempt} onChange={e => setForm(f => ({ ...f, itbis_exempt: e.target.checked }))} />
                {lang === "es" ? "Exento de ITBIS" : "ITBIS exempt"}
              </label>
              <Field label={lang === "es" ? "Tasa ITBIS %" : "ITBIS rate %"} type="number" value={form.itbis_rate} onChange={v => setForm(f => ({ ...f, itbis_rate: v }))} />
              <label style={{ gridColumn: "span 2", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                {lang === "es" ? "Activo (visible en la tienda)" : "Active (visible in store)"}
              </label>
            </div>

            {/* Mostrar en Dashboard */}
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--bd2)" }}>
              <h4 style={{ margin: "0 0 4px", fontSize: 13 }}>
                {lang === "es" ? "📌 Mostrar en Dashboard" : "📌 Show on Dashboard"}
              </h4>
              <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 10 }}>
                {lang === "es"
                  ? "Elige en qué secciones de la página principal aparece este producto."
                  : "Choose which sections of the home page feature this product."}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px solid var(--bd2)", borderRadius: 6, background: form.show_in_trending ? "var(--ac2)" : "transparent", cursor: "pointer" }}>
                  <input type="checkbox" checked={form.show_in_trending} onChange={e => setForm(f => ({ ...f, show_in_trending: e.target.checked }))} />
                  <span>🔥 {lang === "es" ? "Productos populares (Trending)" : "Trending"}</span>
                </label>
                <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px solid var(--bd2)", borderRadius: 6, background: form.show_in_deals ? "var(--ac2)" : "transparent", cursor: "pointer" }}>
                  <input type="checkbox" checked={form.show_in_deals} onChange={e => setForm(f => ({ ...f, show_in_deals: e.target.checked }))} />
                  <span>💰 {lang === "es" ? "Ofertas" : "Offers"}</span>
                </label>
              </div>

              <DashboardPreview
                rows={rows}
                editing={editing}
                form={form}
                lang={lang}
                $={$}
                setRows={setRows}
                toast={toast}
              />
            </div>

            {/* Detalles del producto */}
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--bd2)" }}>
              <h4 style={{ margin: "0 0 10px", fontSize: 13 }}>
                {lang === "es" ? "Detalles del producto" : "Product details"}
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label={lang === "es" ? "Ingredientes" : "Ingredients"} value={form.ingredients} onChange={v => setForm(f => ({ ...f, ingredients: v }))} colSpan={2} multiline />
                <Field label={lang === "es" ? "Modo de preparación / uso" : "Preparation / usage"} value={form.usage} onChange={v => setForm(f => ({ ...f, usage: v }))} colSpan={2} multiline />
                <Field label={lang === "es" ? "Beneficios (uno por línea)" : "Benefits (one per line)"} value={form.benefits} onChange={v => setForm(f => ({ ...f, benefits: v }))} colSpan={2} multiline />
                <Field label={lang === "es" ? "Advertencias" : "Warnings"} value={form.warnings} onChange={v => setForm(f => ({ ...f, warnings: v }))} colSpan={2} multiline />
                <Field label={lang === "es" ? "Alérgenos" : "Allergens"} value={form.allergens} onChange={v => setForm(f => ({ ...f, allergens: v }))} />
                <Field label={lang === "es" ? "Momento ideal / timing" : "Best timing"} value={form.timing} onChange={v => setForm(f => ({ ...f, timing: v }))} />
              </div>
            </div>

            {/* Información nutricional */}
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--bd2)" }}>
              <h4 style={{ margin: "0 0 10px", fontSize: 13 }}>
                {lang === "es" ? "Información nutricional" : "Nutrition facts"}
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                <Field label={lang === "es" ? "Porción" : "Serving"} value={form.nut_serving} onChange={v => setForm(f => ({ ...f, nut_serving: v }))} colSpan={2} />
                <Field label={lang === "es" ? "Calorías" : "Calories"} type="number" value={form.nut_calories} onChange={v => setForm(f => ({ ...f, nut_calories: v }))} />
                <Field label={lang === "es" ? "Proteína (g)" : "Protein (g)"} type="number" value={form.nut_protein} onChange={v => setForm(f => ({ ...f, nut_protein: v }))} />
                <Field label={lang === "es" ? "Carbohidratos (g)" : "Carbs (g)"} type="number" value={form.nut_carbs} onChange={v => setForm(f => ({ ...f, nut_carbs: v }))} />
                <Field label={lang === "es" ? "Grasa (g)" : "Fat (g)"} type="number" value={form.nut_fat} onChange={v => setForm(f => ({ ...f, nut_fat: v }))} />
                <Field label={lang === "es" ? "Azúcar (g)" : "Sugar (g)"} type="number" value={form.nut_sugar} onChange={v => setForm(f => ({ ...f, nut_sugar: v }))} />
                <Field label={lang === "es" ? "Sodio (mg)" : "Sodium (mg)"} type="number" value={form.nut_sodium} onChange={v => setForm(f => ({ ...f, nut_sodium: v }))} />
                <Field label={lang === "es" ? "Fibra (g)" : "Fiber (g)"} type="number" value={form.nut_fiber} onChange={v => setForm(f => ({ ...f, nut_fiber: v }))} />
              </div>
            </div>

            {/* Variantes (sabor / tamaño / etc.) */}
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--bd2)" }}>
              <h4 style={{ margin: "0 0 10px", fontSize: 13 }}>
                {lang === "es" ? "Variantes (sabor, tamaño, etc.)" : "Variants (flavor, size, etc.)"}
              </h4>
              {editing
                ? <VariantsManager productId={editing.id} lang={lang} toast={toast} />
                : <div style={{ fontSize: 11, color: "var(--tx3)", padding: 10, border: "1px dashed var(--bd2)", borderRadius: 6 }}>
                    {lang === "es"
                      ? "Crea el producto primero. Después podrás abrir el editor para añadir variantes."
                      : "Create the product first. Then reopen the editor to add variants."}
                  </div>}
            </div>

            {/* Productos conectados */}
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--bd2)" }}>
              <h4 style={{ margin: "0 0 4px", fontSize: 13 }}>
                {lang === "es" ? "🔗 Conectar con otros productos" : "🔗 Connect with other products"}
              </h4>
              <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 10 }}>
                {lang === "es"
                  ? "Sugiere relacionados, alternativas o bundles al mostrar este producto."
                  : "Surface related items, alternatives or bundles when showing this product."}
              </div>
              {editing
                ? <ProductRelationsManager productId={editing.id} lang={lang} toast={toast} />
                : <div style={{ fontSize: 11, color: "var(--tx3)", padding: 10, border: "1px dashed var(--bd2)", borderRadius: 6 }}>
                    {lang === "es"
                      ? "Crea el producto primero. Después podrás conectarlo con otros."
                      : "Create the product first to link it with others."}
                  </div>}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button className="btn bg bs" onClick={() => setShowForm(false)}>{lang === "es" ? "Cancelar" : "Cancel"}</button>
              <button className="btn bp" onClick={save} disabled={uploading}>
                {editing ? (lang === "es" ? "Guardar cambios" : "Save changes") : (lang === "es" ? "Crear" : "Create")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CROP MODAL */}
      {cropSrc && (
        <div className="qv-overlay" style={{ zIndex: 1200 }} onClick={uploading ? undefined : () => setCropSrc(null)}>
          <div className="qv-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, padding: 20 }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>{lang === "es" ? "Recortar imagen" : "Crop image"}</h3>
            <div style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 12 }}>
              {lang === "es" ? "Arrastra para mover · zoom para ajustar" : "Drag to move · zoom to adjust"}
            </div>
            <div onPointerDown={onCropPointerDown} onPointerMove={onCropPointerMove} onPointerUp={onCropPointerUp} onPointerCancel={onCropPointerUp}
              style={{ position: "relative", width: 320, height: 320, margin: "0 auto", background: "#000", overflow: "hidden", borderRadius: 8, border: "1px solid var(--bd2)", touchAction: "none", cursor: "grab", userSelect: "none" }}>
              <img ref={cropImgRef} src={cropSrc} draggable={false} alt=""
                style={{ position: "absolute", left: "50%", top: "50%", maxWidth: "100%", maxHeight: "100%",
                  transform: `translate(-50%, -50%) translate(${cropOffset.x}px, ${cropOffset.y}px) scale(${cropZoom})`, transformOrigin: "center center", pointerEvents: "none" }} />
              <div style={{ position: "absolute", inset: 0, boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.5)", pointerEvents: "none" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
              <span style={{ fontSize: 11, color: "var(--tx3)" }}>Zoom</span>
              <input type="range" min={1} max={4} step={0.05} value={cropZoom} onChange={e => setCropZoom(parseFloat(e.target.value))} style={{ flex: 1 }} />
              <button className="btn bs" style={{ fontSize: 10 }} onClick={() => { setCropZoom(1); setCropOffset({ x: 0, y: 0 }); }}>Reset</button>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button className="btn bg bs" onClick={() => setCropSrc(null)} disabled={uploading}>{lang === "es" ? "Cancelar" : "Cancel"}</button>
              <button className="btn bp" onClick={confirmCrop} disabled={uploading}>
                {uploading ? (lang === "es" ? "Subiendo…" : "Uploading…") : (lang === "es" ? "Recortar y subir" : "Crop & upload")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV PREVIEW MODAL */}
      {csvPreview && (
        <div className="qv-overlay" style={{ zIndex: 1100 }} onClick={() => setCsvPreview(null)}>
          <div className="qv-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720, padding: 22 }}>
            <button className="qv-close" onClick={() => setCsvPreview(null)}>{I.x}</button>
            <h3 style={{ marginTop: 0 }}>{lang === "es" ? `Vista previa CSV — ${csvPreview.length} productos` : `CSV preview — ${csvPreview.length} products`}</h3>
            <div style={{ fontSize: 11, color: "var(--tx3)", marginBottom: 10 }}>
              {lang === "es" ? "Columnas reconocidas: name, brand, sku, price, compare_at_price, stock, category, description, image_url, is_active" : "Recognized columns: name, brand, sku, price, compare_at_price, stock, category, description, image_url, is_active"}
            </div>
            <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid var(--bd2)", borderRadius: 6 }}>
              <table className="tbl" style={{ fontSize: 11 }}>
                <thead><tr><th>Name</th><th>Brand</th><th>Price</th><th>Stock</th><th>Category</th></tr></thead>
                <tbody>
                  {csvPreview.slice(0, 50).map((r, i) => (
                    <tr key={i}><td>{r.name}</td><td>{r.brand}</td><td>{r.price}</td><td>{r.stock || r.stock_quantity}</td><td>{r.category}</td></tr>
                  ))}
                </tbody>
              </table>
              {csvPreview.length > 50 && <div style={{ padding: 8, fontSize: 10, color: "var(--tx3)" }}>… +{csvPreview.length - 50} más</div>}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button className="btn bg bs" onClick={() => setCsvPreview(null)}>{lang === "es" ? "Cancelar" : "Cancel"}</button>
              <button className="btn bp" onClick={confirmCsvImport}>{lang === "es" ? `Importar ${csvPreview.length}` : `Import ${csvPreview.length}`}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────── helpers ───────
const inputSm = { fontSize: 11, padding: "5px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)", color: "var(--tx)", fontFamily: "var(--f)" };
const lblStyle = { fontSize: 10, color: "var(--tx3)", fontWeight: 600, display: "block", marginBottom: 6 };
const miniBtn = { fontSize: 9, padding: "2px 6px", height: "auto" };

function Field({ label, value, onChange, type = "text", colSpan = 1, multiline = false }) {
  const style = { gridColumn: `span ${colSpan}` };
  const inputStyle = { width: "100%", fontSize: 12, padding: "6px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)", color: "var(--tx)", fontFamily: "var(--f)" };
  return (
    <div style={style}>
      <label style={lblStyle}>{label}</label>
      {multiline
        ? <textarea rows={2} value={value} onChange={e => onChange(e.target.value)} style={inputStyle} />
        : <input type={type} value={value} onChange={e => onChange(e.target.value)} style={inputStyle} />}
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  const inputStyle = { width: "100%", fontSize: 12, padding: "6px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)", color: "var(--tx)", fontFamily: "var(--f)" };
  return (
    <div>
      <label style={lblStyle}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
        <option value="">—</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function InlineInput({ value, onChange, onSave, onCancel }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <span style={{ display: "inline-flex", gap: 3, alignItems: "center" }}>
      <input ref={ref} type="number" value={value} onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
        onBlur={onSave}
        style={{ width: 70, fontSize: 11, padding: "2px 5px", border: "1px solid var(--bp)", borderRadius: 4, background: "var(--bg2)", color: "var(--tx)" }} />
      <button className="btn bp bs" style={miniBtn} onMouseDown={e => e.preventDefault()} onClick={onSave}>✓</button>
      <button className="btn bs" style={{ ...miniBtn, color: "var(--tx3)" }} onMouseDown={e => e.preventDefault()} onClick={onCancel}>×</button>
    </span>
  );
}

function parseCsvLine(line) {
  const out = []; let cur = ""; let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQ = false;
      else cur += c;
    } else {
      if (c === ",") { out.push(cur); cur = ""; }
      else if (c === '"') inQ = true;
      else cur += c;
    }
  }
  out.push(cur);
  return out;
}
function csvCell(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function download(name, csv) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

/**
 * Live preview of how Trending / Ofertas look on the Dashboard, reflecting
 * (a) every product already saved with the corresponding flag in the DB and
 * (b) the in-form overrides of the product currently being edited.
 */
function DashboardPreview({ rows, editing, form, lang, $, setRows, toast }) {
  const [device, setDevice] = useState("desktop"); // desktop | tablet | mobile
  const [dragKey, setDragKey] = useState<string | null>(null); // `${kind}:${id}`
  const [grabbedKey, setGrabbedKey] = useState<string | null>(null); // keyboard "grab"
  const [liveMsg, setLiveMsg] = useState<string>("");
  const announce = (msg: string) => setLiveMsg(msg);

  // Apply in-form overrides on top of the persisted rows so the preview
  // updates instantly as the admin toggles the checkboxes / changes price.
  const editedRow = {
    id: editing?.id || "__draft__",
    name: form.name || (lang === "es" ? "(sin nombre)" : "(untitled)"),
    brand: form.brand || "",
    price: parseFloat(form.price) || 0,
    compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
    image_url: (form.images && form.images[0]) || null,
    is_active: !!form.is_active,
    show_in_trending: !!form.show_in_trending,
    show_in_deals: !!form.show_in_deals,
  };

  const merged = (() => {
    const others = (rows || []).filter(r => r.id !== editing?.id);
    return editing ? [editedRow, ...others] : [editedRow, ...others];
  })();

  const byOrder = (k: string) => (a: any, b: any) => (a[k] ?? 0) - (b[k] ?? 0);
  // Sourced from persisted ordering columns; the in-form draft falls back to 0
  // so it slots in at the top until saved & persisted.
  const trending = merged
    .filter(p => p.is_active && p.show_in_trending)
    .slice()
    .sort(byOrder("trending_order"))
    .slice(0, 6);
  const deals = merged
    .filter(p => p.is_active && p.show_in_deals)
    .slice()
    .sort(byOrder("deals_order"))
    .slice(0, 6);

  // Persist a new order for the given section. Optimistic local update + DB batch.
  const persistOrder = async (kind: "trending" | "deals", ordered: any[]) => {
    const field = kind === "trending" ? "trending_order" : "deals_order";
    // Optimistic update of parent rows
    setRows?.(prev => prev.map(r => {
      const idx = ordered.findIndex(o => o.id === r.id);
      return idx >= 0 ? { ...r, [field]: idx } : r;
    }));
    // Persist to DB (skip the unsaved draft pseudo-row)
    const updates = ordered
      .map((p, i) => ({ id: p.id, idx: i }))
      .filter(u => u.id && u.id !== "__draft__");
    const results = await Promise.all(
      updates.map(u => supabase.from("products").update({ [field]: u.idx }).eq("id", u.id))
    );
    const errored = results.find(r => r.error);
    if (errored?.error) {
      toast?.(`❌ ${errored.error.message}`);
    }
  };

  const onDrop = (kind: "trending" | "deals", list: any[], targetId: string) => {
    if (!dragKey) return;
    const [dKind, dId] = dragKey.split(":");
    if (dKind !== kind || dId === targetId) { setDragKey(null); return; }
    const from = list.findIndex(p => String(p.id) === dId);
    const to = list.findIndex(p => String(p.id) === targetId);
    if (from < 0 || to < 0) { setDragKey(null); return; }
    const next = list.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDragKey(null);
    persistOrder(kind, next);
  };

  // Keyboard reordering: ArrowUp/Left moves item earlier, ArrowDown/Right moves it
  // later. Space/Enter toggles a "grabbed" announcement state for screen readers.
  // Esc releases the grab. Works regardless of grab state for keyboard users.
  const moveByKeyboard = (kind: "trending" | "deals", list: any[], id: string, delta: number) => {
    const from = list.findIndex(p => String(p.id) === id);
    if (from < 0) return;
    const to = Math.max(0, Math.min(list.length - 1, from + delta));
    if (to === from) return;
    const next = list.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    persistOrder(kind, next);
    announce(
      lang === "es"
        ? `Movido a la posición ${to + 1} de ${list.length}`
        : `Moved to position ${to + 1} of ${list.length}`
    );
    // Restore focus to the same item at its new index after re-render.
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-sortable-key="${kind}:${id}"]`);
      el?.focus();
    });
  };

  const deviceCols = { desktop: "repeat(3, 1fr)", tablet: "repeat(2, 1fr)", mobile: "repeat(2, 1fr)" };
  const deviceWidth = { desktop: "100%", tablet: "520px", mobile: "320px" };
  const deviceLabel = { desktop: "🖥️ Desktop", tablet: "📱 Tablet", mobile: "📲 Mobile" };

  const Section = ({ kind, title, list, emptyMsg, showCompare }: any) => (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--tx)" }}>{title}</span>
        <span style={{ fontSize: 9, color: "var(--tx3)" }}>
          {list.length}/6 · {lang === "es" ? "arrastra o usa flechas ←→↑↓" : "drag or use ←→↑↓ arrows"}
        </span>
      </div>
      {list.length === 0
        ? <div style={{ fontSize: 10, color: "var(--tx3)", fontStyle: "italic", padding: "10px 8px", border: "1px dashed var(--bd2)", borderRadius: 6 }}>{emptyMsg}</div>
        : (
          <div
            role="list"
            aria-label={typeof title === "string" ? title : undefined}
            style={{ display: "grid", gridTemplateColumns: deviceCols[device], gap: 6 }}
          >
            {list.map(p => {
              const isDraft = p.id === editing?.id || p.id === "__draft__";
              const myKey = `${kind}:${p.id}`;
              const isDragging = dragKey === myKey;
              const isGrabbed = grabbedKey === myKey;
              const idx = list.findIndex((x: any) => x.id === p.id);
              const ariaLabel = lang === "es"
                ? `${p.name}. Posición ${idx + 1} de ${list.length}. Pulsa flechas para reordenar, Espacio para anunciar selección, Escape para soltar.`
                : `${p.name}. Position ${idx + 1} of ${list.length}. Press arrow keys to reorder, Space to announce selection, Escape to release.`;
              const discount = showCompare && p.compare_at_price && p.compare_at_price > p.price
                ? Math.round((1 - p.price / p.compare_at_price) * 100)
                : 0;
              return (
                <div
                  key={p.id}
                  role="listitem"
                  tabIndex={0}
                  aria-roledescription={lang === "es" ? "elemento ordenable" : "sortable item"}
                  aria-label={ariaLabel}
                  aria-grabbed={isGrabbed}
                  data-sortable-key={myKey}
                  draggable
                  onDragStart={() => setDragKey(myKey)}
                  onDragOver={e => { e.preventDefault(); }}
                  onDrop={e => { e.preventDefault(); onDrop(kind, list, String(p.id)); }}
                  onDragEnd={() => setDragKey(null)}
                  title={lang === "es" ? "Arrastra o usa las flechas para reordenar" : "Drag or use arrow keys to reorder"}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                      e.preventDefault();
                      moveByKeyboard(kind, list, String(p.id), -1);
                    } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                      e.preventDefault();
                      moveByKeyboard(kind, list, String(p.id), 1);
                    } else if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      const next = grabbedKey === myKey ? null : myKey;
                      setGrabbedKey(next);
                      announce(next
                        ? (lang === "es" ? `${p.name} seleccionado. Usa las flechas para mover.` : `${p.name} grabbed. Use arrow keys to move.`)
                        : (lang === "es" ? "Soltado" : "Released"));
                    } else if (e.key === "Escape" && grabbedKey === myKey) {
                      e.preventDefault();
                      setGrabbedKey(null);
                      announce(lang === "es" ? "Soltado" : "Released");
                    }
                  }}
                  style={{
                    position: "relative",
                    border: isGrabbed ? "2px dashed var(--bp)" : isDraft ? "2px solid var(--bp)" : "1px solid var(--bd2)",
                    borderRadius: 6,
                    padding: 6,
                    background: "var(--bg2)",
                    overflow: "hidden",
                    cursor: "grab",
                    opacity: isDragging ? 0.4 : 1,
                    boxShadow: isGrabbed
                      ? "0 0 0 2px var(--bp) inset"
                      : dragKey && !isDragging && dragKey.startsWith(`${kind}:`)
                        ? "0 0 0 1px var(--bp) inset"
                        : "none",
                    transition: "opacity .15s, box-shadow .15s",
                  }}
                >
                  <span aria-hidden="true" style={{ position: "absolute", top: 2, left: 2, fontSize: 9, color: "var(--tx3)", lineHeight: 1, userSelect: "none" }}>⋮⋮</span>
                  {isDraft && (
                    <span style={{ position: "absolute", top: 2, right: 2, fontSize: 7.5, fontWeight: 700, background: "var(--bp)", color: "#fff", padding: "1px 4px", borderRadius: 3, letterSpacing: 0.3 }}>
                      {lang === "es" ? "ESTE" : "THIS"}
                    </span>
                  )}
                  <div style={{ width: "100%", aspectRatio: "1", background: "var(--bg)", borderRadius: 4, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 4, padding: 4 }}>
                    {p.image_url
                      ? <img src={p.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      : <span style={{ fontSize: 18, color: "var(--tx3)" }}>📦</span>}
                  </div>
                  <div style={{ fontSize: 9.5, fontWeight: 600, color: "var(--tx)", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                    {p.name}
                  </div>
                  {p.brand && <div style={{ fontSize: 8.5, color: "var(--tx3)", marginTop: 1 }}>{p.brand}</div>}
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: 3, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "var(--tx)" }}>{fmtDop(p.price)}</span>
                    {showCompare && p.compare_at_price && p.compare_at_price > p.price && (
                      <>
                        <span style={{ fontSize: 8.5, color: "var(--tx3)", textDecoration: "line-through" }}>{fmtDop(p.compare_at_price)}</span>
                        <span style={{ fontSize: 8.5, fontWeight: 700, color: "hsl(var(--primary))" }}>-{discount}%</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );

  return (
    <div style={{ marginTop: 12, padding: 10, background: "var(--bg)", border: "1px solid var(--bd2)", borderRadius: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
        <div style={{ fontSize: 10, color: "var(--tx3)", fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>
          {lang === "es" ? "🔴 Previsualización en vivo del Dashboard" : "🔴 Live dashboard preview"}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {["desktop", "tablet", "mobile"].map(d => (
            <button
              key={d}
              onClick={() => setDevice(d)}
              style={{
                fontSize: 10,
                padding: "3px 8px",
                borderRadius: 5,
                border: "1px solid var(--bd2)",
                background: device === d ? "var(--bp)" : "var(--bg2)",
                color: device === d ? "#fff" : "var(--tx)",
                cursor: "pointer",
                fontWeight: device === d ? 700 : 500,
              }}
              title={d}
            >
              {deviceLabel[d]}
            </button>
          ))}
        </div>
      </div>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}
      >
        {liveMsg}
      </div>
      <div style={{ overflow: "auto", paddingBottom: 4 }}>
        <div style={{ maxWidth: deviceWidth[device], margin: "0 auto" }}>
          <div style={{ display: "grid", gap: 12 }}>
            <Section
              kind="trending"
              title={lang === "es" ? "🔥 Trending" : "🔥 Trending"}
              list={trending}
              showCompare={false}
              emptyMsg={lang === "es" ? "Ningún producto marcado como Trending todavía." : "No products marked as Trending yet."}
            />
            <Section
              kind="deals"
              title={lang === "es" ? "💰 Ofertas" : "💰 Offers"}
              list={deals}
              showCompare={true}
              emptyMsg={lang === "es" ? "Ningún producto marcado como Oferta todavía." : "No products marked as Offers yet."}
            />
          </div>
        </div>
      </div>
    </div>
  );
}